import { useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  HStack,
  MenuItem,
  Skeleton,
  Stack,
  UnorderedList,
  useDisclosure,
  useEditableControls,
  useToast,
} from "@chakra-ui/react";
import { LuTrash2 } from "react-icons/lu";
import { useNavigate } from "react-router";
import CDS from "design-system";

import CaminoBreadcrumbs from "components/Breadcrumbs";
import CaminoPencil from "components/Icon/CaminoPencil";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { pluralize } from "helpers/string";
import { useCustomFormDeleteImpactQuery, useDeleteCustomForm } from "../hooks";
import type { CustomForm } from "../types";
import AutosaveIndicator, { type AutosaveState } from "./AutosaveIndicator";

interface Props {
  form: CustomForm;
  autosave: AutosaveState;
  onNameChange: (nextName: string) => void;
  onPreview: () => void;
}

function HoverPencilIcon() {
  const { isEditing } = useEditableControls();
  if (isEditing) return null;
  return (
    <CaminoPencil
      color="content.tertiary"
      boxSize={3.5}
      opacity={0}
      _groupHover={{ opacity: 1 }}
      transition="opacity 0.15s"
    />
  );
}

function useSubmitOrRevert(current: string, onSave: (next: string) => void) {
  return (next: string) => {
    const trimmed = next.trim();
    if (!trimmed) return;
    if (trimmed === current) return;
    onSave(trimmed);
  };
}

interface DeleteDialogProps {
  form: CustomForm;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

function DeleteConfirmDialog({ form, isOpen, onClose, onConfirm, isDeleting }: DeleteDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { data: impact, isLoading } = useCustomFormDeleteImpactQuery(form.id, isOpen);

  const nothing = !isLoading && impact && impact.taskTemplatesCount === 0 && impact.pendingTasksCount === 0;

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Delete this form?
          </AlertDialogHeader>
          <AlertDialogBody>
            {isLoading || !impact ? (
              <Skeleton height="60px" />
            ) : (
              <Stack spacing={3}>
                {nothing ? (
                  <CDS.Text14>
                    This form isn&apos;t attached to any task templates and has no pending tasks.
                    Existing responses stay in place.
                  </CDS.Text14>
                ) : (
                  <>
                    <CDS.Text14>Deleting <strong>{form.name}</strong> will also:</CDS.Text14>
                    <UnorderedList spacing={1} pl={5}>
                      {impact.taskTemplatesCount > 0 && (
                        <CDS.Text14 as="li">
                          Orphan {impact.taskTemplatesCount} {pluralize("task template", impact.taskTemplatesCount)} that
                          reference this form (their task action will be cleared so you can reattach or reassign).
                        </CDS.Text14>
                      )}
                      {impact.pendingTasksCount > 0 && (
                        <CDS.Text14 as="li">
                          Delete {impact.pendingTasksCount} pending {pluralize("task", impact.pendingTasksCount)} on live
                          journeys that haven&apos;t been submitted yet.
                        </CDS.Text14>
                      )}
                    </UnorderedList>
                    <CDS.Text12 color="content.tertiary">
                      Completed responses and their tasks stay in place.
                    </CDS.Text12>
                  </>
                )}
              </Stack>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <HStack spacing={3}>
              <CDS.Button
                ref={cancelRef}
                intent={CDS.Button.intent.secondary}
                variant={CDS.Button.variant.ghost}
                size="sm"
                onClick={onClose}
              >
                Cancel
              </CDS.Button>
              <CDS.Button
                intent={CDS.Button.intent.error}
                variant={CDS.Button.variant.solid}
                size="sm"
                onClick={onConfirm}
                loading={isDeleting}
                disabled={isLoading}
              >
                Yes, delete
              </CDS.Button>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default function FormHeaderStrip({ form, autosave, onNameChange, onPreview }: Props) {
  const commitName = useSubmitOrRevert(form.name, onNameChange);
  const toast = useToast();
  const navigate = useNavigate();
  const { mutate: deleteForm, isPending: isDeleting } = useDeleteCustomForm();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleDelete = () => {
    deleteForm(form.id, {
      onSuccess: () => {
        onClose();
        toast({ title: "Form deleted.", status: "success" });
        navigate(PEOPLE_TEAM_ROUTES.customForms.index);
      },
      onError: () => toast({ title: "Couldn't delete the form.", status: "error" }),
    });
  };

  return (
    <Box borderBottomWidth="1px" borderColor="border.subtle" pb={4} mb={4}>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "Forms", toUrl: PEOPLE_TEAM_ROUTES.customForms.index },
          { name: form.name },
        ]}
      />

      <Flex align="center" justify="space-between" gap={4} mt={3}>
        <Editable
          defaultValue={form.name}
          onSubmit={commitName}
          textStyle="headerL"
          submitOnBlur
          selectAllOnFocus={false}
          display="flex"
          alignItems="center"
          gap={2}
          role="group"
          minW={0}
          flex={1}
        >
          <EditablePreview
            px={1}
            _hover={{ bg: "bg.tertiary", cursor: "text" }}
            borderRadius="base"
          />
          <EditableInput px={1} />
          <HoverPencilIcon />
        </Editable>

        <HStack spacing={3}>
          <AutosaveIndicator state={autosave} />
          <CDS.Button
            intent={CDS.Button.intent.indigo}
            variant={CDS.Button.variant.outline}
            size="sm"
            onClick={onPreview}
          >
            Preview
          </CDS.Button>
          <VThreeDotMenu ariaLabel="Form actions" size="sm">
            <MenuItem icon={<MenuItemIcon icon={LuTrash2} />} onClick={onOpen}>
              Delete form
            </MenuItem>
          </VThreeDotMenu>
        </HStack>
      </Flex>

      <DeleteConfirmDialog
        form={form}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
}
